import { memo, Suspense } from 'react'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { TrendingUp, TrendingDown, Minus } from 'lucide-react'
import type { DailyMetric } from '@/lib/jira/metrics'

interface JiraCompletionTrendChartProps {
  data: DailyMetric[]
  title?: string
  height?: number
  loading?: boolean
  className?: string
  showTarget?: boolean
  targetValue?: number
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 shadow-lg">
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{label}</p>
      <p className="text-sm font-semibold text-gray-900 dark:text-white">
        {payload[0].value}%
      </p>
    </div>
  )
}

const ChartContent = memo(function ChartContent({
  data,
  height = 280,
  showTarget = true,
  targetValue = 70,
}: Pick<JiraCompletionTrendChartProps, 'data' | 'height' | 'showTarget' | 'targetValue'>) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="completionGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
        <XAxis
          dataKey="date"
          tick={{ fontSize: 11 }}
          className="text-gray-500 dark:text-gray-400"
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          domain={[0, 100]}
          tick={{ fontSize: 11 }}
          className="text-gray-500 dark:text-gray-400"
          axisLine={false}
          tickLine={false}
          tickFormatter={(value) => `${value}%`}
        />
        <Tooltip content={<CustomTooltip />} />
        {showTarget && (
          <ReferenceLine
            y={targetValue}
            stroke="#22c55e"
            strokeDasharray="5 5"
            strokeWidth={2}
            label={{
              value: `Hedef: ${targetValue}%`,
              position: 'right',
              fontSize: 11,
              fill: '#22c55e',
            }}
          />
        )}
        <Area
          type="monotone"
          dataKey="value"
          stroke="#3b82f6"
          strokeWidth={2.5}
          fill="url(#completionGradient)"
          dot={false}
          activeDot={{ r: 6, fill: '#3b82f6', stroke: '#fff', strokeWidth: 2 }}
        />
      </AreaChart>
    </ResponsiveContainer>
  )
})

const LoadingState = memo(function LoadingState({ height }: { height: number }) {
  return (
    <div
      className="flex items-center justify-center bg-gray-50 dark:bg-gray-800/50 rounded-lg animate-pulse"
      style={{ height }}
    >
      <div className="flex flex-col items-center gap-2">
        <div className="w-8 h-8 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
        <span className="text-sm text-gray-400">Yükleniyor...</span>
      </div>
    </div>
  )
})

const JiraCompletionTrendChart = memo(function JiraCompletionTrendChart({
  data,
  title = 'Tamamlama Oranı Trendi',
  height = 280,
  loading = false,
  className = '',
  showTarget = true,
  targetValue = 70,
}: JiraCompletionTrendChartProps) {
  // Trend hesapla
  const trend = data.length >= 2
    ? data[data.length - 1].value - data[0].value
    : 0

  const TrendIcon = trend > 3 ? TrendingUp : trend < -3 ? TrendingDown : Minus
  const trendColor = trend > 3 ? 'text-green-500' : trend < -3 ? 'text-red-500' : 'text-gray-400'

  // Son değer
  const currentValue = data.length > 0 ? data[data.length - 1].value : 0

  return (
    <Card variant="default" padding="md" className={className}>
      <CardHeader compact>
        <div className="flex items-center justify-between">
          <CardTitle compact className="flex items-center gap-2">
            <div className="p-1.5 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <TrendingUp className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            </div>
            {title}
          </CardTitle>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{currentValue}%</p>
              <div className={`flex items-center gap-1 text-xs ${trendColor}`}>
                <TrendIcon className="h-3 w-3" />
                <span>{trend > 0 ? '+' : ''}{trend.toFixed(0)}%</span>
              </div>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent compact>
        {loading ? (
          <LoadingState height={height} />
        ) : (
          <Suspense fallback={<LoadingState height={height} />}>
            <ChartContent
              data={data}
              height={height}
              showTarget={showTarget}
              targetValue={targetValue}
            />
          </Suspense>
        )}
      </CardContent>
    </Card>
  )
})

export default JiraCompletionTrendChart

